import { PropsWithChildren, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useModalState } from '@/hooks/useModalState';
import { EntityDocumentViewer_FilePageFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { DocumentDetails_DocumentSummaryFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { SelectableDocument } from '@/modules/documents/ManageDocumentAmendments/types';
import type { PDFSource } from '@/modules/pdf/PDFViewer';

import {
  DocumentDetailsContext,
  DocumentDetailsContextValue,
} from './DocumentDetails.context';
import { useAutoSelectFirstSummary } from './hooks/useAutoSelectFirstSummary';
import { useTransitionToNewestSummary } from './hooks/useTransitionToNewestSummary';

interface DocumentDetailsProviderProps {
  documentId: string;
  source: PDFSource | null;
  documentFilePages: EntityDocumentViewer_FilePageFragment[];
  associatedDocuments: SelectableDocument[];
  summaries: DocumentDetails_DocumentSummaryFragment[];
  setIsGeneratingAiSummaryOverride: (
    isGeneratingAiSummaryOverride: boolean
  ) => void;
  transitionToNewSummaryWhenAvailable: boolean;
  setTransitionToNewSummaryWhenAvailable: (value: boolean) => void;
}

export function DocumentDetailsProvider({
  children,
  documentId,
  source,
  documentFilePages,
  associatedDocuments,
  summaries,
  setIsGeneratingAiSummaryOverride,
  transitionToNewSummaryWhenAvailable,
  setTransitionToNewSummaryWhenAvailable,
}: PropsWithChildren<DocumentDetailsProviderProps>) {
  const [
    { isModalOpen: isEditSummaryTrowserOpen },
    { openModal: openEditSummaryTrowser, closeModal: closeEditSummaryTrowser },
  ] = useModalState();

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedSummaryId = searchParams.get('summaryId');

  const setSelectedSummaryId = useCallback(
    (summaryId: string) => {
      setSearchParams((params) => {
        params.set('summaryId', summaryId);
        return params;
      });
    },
    [setSearchParams]
  );

  const preferredSummaryId = useMemo(() => {
    const defaultSummary = summaries.find(
      (s) => s.generatedFromPrompt?.isDefaultForDocType
    );
    return defaultSummary?.id ?? summaries[0]?.id;
  }, [summaries]);
  const effectiveSelectedSummaryId = selectedSummaryId || preferredSummaryId;

  // When loading the page, automatically select the preferred summary
  // (default prompt summary, or first) if no summary is selected
  useAutoSelectFirstSummary({
    firstSummaryId: preferredSummaryId,
    selectedSummaryId,
    setSearchParams,
  });

  // When a new summary is available, potentially transition to it
  useTransitionToNewestSummary({
    shouldTransition: transitionToNewSummaryWhenAvailable,
    summaries,
    onTransition: (summaryId) => {
      setSelectedSummaryId(summaryId);
      setTransitionToNewSummaryWhenAvailable(false);
    },
  });

  // Derive the current summary based on selectedSummaryId or default to first summary
  const documentSummary = useMemo(() => {
    if (!summaries || summaries.length === 0) return null;

    if (effectiveSelectedSummaryId) {
      const found = summaries.find((s) => s.id === effectiveSelectedSummaryId);
      if (found) return found;
    }

    // Fallback to first summary
    return summaries[0];
  }, [summaries, effectiveSelectedSummaryId]);

  const value: DocumentDetailsContextValue = {
    setIsGeneratingAiSummaryOverride,
    isEditSummaryTrowserOpen,
    openEditSummaryTrowser,
    closeEditSummaryTrowser,
    documentFilePages,
    documentSummary,
    documentId,
    source,
    associatedDocuments,
    summaries,
    selectedSummaryId,
    setSelectedSummaryId,
    transitionToNewSummaryWhenAvailable,
    setTransitionToNewSummaryWhenAvailable,
  };

  return (
    <DocumentDetailsContext.Provider value={value}>
      {children}
    </DocumentDetailsContext.Provider>
  );
}
